/* Cordings header behaviour: sticky/shrink on scroll, help dropdown (touch/keyboard),
   mobile menu drawer, contact slide-up sheet, mobile search toggle. */
class CordingsHeader extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-overlay]');
    this.drawer = this.querySelector('[data-drawer]');
    this.sheet = this.querySelector('[data-sheet]');
    this.help = this.querySelector('[data-help]');
    this.msearch = this.querySelector('[data-msearch]');
    this.compact = this.querySelector('[data-compact]');
    this.bar = this.querySelector('.cds-header__bar');
    this._revealAt = (this.bar || {}).offsetHeight || 120;
    this._megaItems = Array.from(this.querySelectorAll('.cds-mega-item'));

    // Expose the header height so a transparent header can pull the hero up under it.
    this._setHeaderHeight = () => {
      if (this.bar) document.documentElement.style.setProperty('--cds-header-height', this.bar.offsetHeight + 'px');
    };
    this._setHeaderHeight();
    window.addEventListener('resize', this._setHeaderHeight, { passive: true });

    this._onScroll = this._onScroll.bind(this);
    this._onKey = this._onKey.bind(this);

    // Sticky / shorter-on-scroll (only when enabled)
    this._ticking = false;
    if (this.dataset.sticky !== 'false') {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    // Mobile menu drawer
    this._bindToggle('[data-open-drawer]', () => this.openPanel(this.drawer));
    // Contact slide-up sheet (also the desktop help button on touch)
    this._bindToggle('[data-open-sheet]', () => this.openPanel(this.sheet));
    // Mobile search
    this._bindToggle('[data-open-search]', () => this._toggleSearch());

    // Close controls
    this.querySelectorAll('[data-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeAll())
    );
    if (this.overlay) this.overlay.addEventListener('click', () => this.closeAll());
    document.addEventListener('keydown', this._onKey);

    // Mega menus
    this._initMegaMenus();

    // Help dropdown: click toggles on touch / keyboard (hover handled in CSS)
    if (this.help) {
      const trigger = this.help.querySelector('[data-help-trigger]');
      if (trigger) {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const open = this.help.classList.toggle('is-open');
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.classList.toggle('is-solid', open);
        });
      }
      document.addEventListener('click', (e) => {
        if (!this.help.contains(e.target)) this.help.classList.remove('is-open');
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._setHeaderHeight);
    document.removeEventListener('keydown', this._onKey);
  }

  _bindToggle(selector, fn) {
    this.querySelectorAll(selector).forEach((el) =>
      el.addEventListener('click', (e) => {
        e.preventDefault();
        fn();
      })
    );
  }

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      if (this.compact) this.compact.classList.toggle('is-visible', window.scrollY > this._revealAt);
      this._ticking = false;
    });
  }

  _onKey(e) {
    if (e.key === 'Escape') this.closeAll();
  }

  _toggleSearch() {
    if (!this.msearch) return;
    const open = this.msearch.classList.toggle('is-open');
    if (open) {
      const input = this.msearch.querySelector('input');
      if (input) input.focus();
    }
  }

  openPanel(panel) {
    if (!panel) return;
    this.closeAll();
    panel.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-active');
    this.classList.add('is-solid');
    document.body.style.overflow = 'hidden';
  }

  closeAll() {
    if (this.drawer) this.drawer.classList.remove('is-open');
    if (this.sheet) this.sheet.classList.remove('is-open');
    if (this.msearch) this.msearch.classList.remove('is-open');
    if (this.help) this.help.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-active');
    this.classList.remove('is-solid');
    document.body.style.overflow = '';
    this._closeMegaMenus();
  }

  _initMegaMenus() {
    if (!this._megaItems.length) return;

    this._megaItems.forEach((item) => {
      const trigger = item.querySelector('[data-mega-trigger]');
      if (!trigger) return;

      // Hover open/close is handled by CSS :hover on .cds-mega-item.
      // JS handles click/touch toggle and aria state only.
      trigger.addEventListener('click', () => {
        if (item.classList.contains('is-open')) {
          this._closeMegaItem(item);
        } else {
          this._closeMegaMenus();
          this._openMegaItem(item);
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this._closeMegaMenus();
    });
  }

  _openMegaItem(item) {
    this._megaItems.forEach((other) => {
      if (other !== item) this._closeMegaItem(other);
    });
    item.classList.add('is-open');
    const trigger = item.querySelector('[data-mega-trigger]');
    const panel = item.querySelector('.cds-mega__panel');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (panel) panel.setAttribute('aria-hidden', 'false');
    this.classList.add('is-solid');
  }

  _closeMegaItem(item) {
    item.classList.remove('is-open');
    const trigger = item.querySelector('[data-mega-trigger]');
    const panel = item.querySelector('.cds-mega__panel');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.setAttribute('aria-hidden', 'true');
    if (!this._megaItems.some((i) => i.classList.contains('is-open'))) {
      this.classList.remove('is-solid');
    }
  }

  _closeMegaMenus() {
    this._megaItems.forEach((item) => this._closeMegaItem(item));
  }
}
customElements.define('cordings-header', CordingsHeader);
